/**
 * Following the layout shared by the other people on the same core. When someone else's page
 * lays the graph out or drags a node, its positions arrive (`project.nodes_moved`): this page
 * stops its own simulation and glides each node there, so everyone looks at one layout.
 *
 * In 3D the shared (2D) positions only replace the ones kept aside for going back to 2D; the
 * 3D picture, with its own depths, is this page's own view.
 */
import type { Scene } from "./index";
import type { SceneMemory, SimNode } from "./types";
import { centreOf } from "./folds";

/** How quickly a node glides to its shared place: about two thirds of the way in this many ms. */
export const FOLLOW_GLIDE_MS = 90;

/** Gliding to shared positions. */
export interface SceneFollow {
  /**
   * Sends the nodes listed to these places (gliding there), stops this page's simulation and
   * makes it a follower. Nodes for which `skip` says so (the ones held by the pointer) stay.
   * Returns how many nodes were sent somewhere.
   */
  follow(positions: Record<string, [number, number]>, skip?: (id: string) => boolean): number;
  /** Moves gliding nodes on by `dtMs`. Returns `true` while some are still on their way. */
  stepFollow(dtMs: number): boolean;
}

/** The following of `scene`. */
export function follow(scene: Scene, memory: SceneMemory): SceneFollow {
  /** The nodes gliding now. */
  const gliding = new Set<string>();
  return {
    follow(positions, skip) {
      let sent = 0;
      /** Stand-ins of folded groups whose hidden nodes moved: they glide to their new middle. */
      const carried = new Set<SimNode>();
      for (const [id, [x, y]] of Object.entries(positions)) {
        const n = scene.byId.get(id);
        if (!n || n.fixed || skip?.(id)) continue;
        sent++;
        if (memory.flatLayout) {
          memory.flatLayout.set(id, [x, y]);
          continue;
        }
        if (n.foldedIn?.fold) {
          Object.assign(n, { x, y, goal: null });
          if (!n.foldedIn.fixed) carried.add(n.foldedIn);
          continue;
        }
        n.goal = { x, y };
        Object.assign(n, { vx: 0, vy: 0, placed: false });
        gliding.add(id);
      }
      for (const standIn of carried) {
        const fold = standIn.fold!;
        const [x, y] = centreOf(fold.members);
        for (const m of fold.members) fold.offsets.set(m.id, [m.x - x, m.y - y]);
        standIn.goal = { x, y };
        Object.assign(standIn, { vx: 0, vy: 0, placed: false });
        gliding.add(standIn.id);
      }
      if (sent && !memory.flatLayout) {
        scene.alpha = 0;
        scene.lead = false;
      }
      scene.redraw = true;
      return sent;
    },

    stepFollow(dtMs) {
      if (!gliding.size) return false;
      const t = 1 - Math.exp(-Math.max(dtMs, 0) / FOLLOW_GLIDE_MS);
      for (const id of gliding) {
        const n = scene.byId.get(id);
        const goal = n?.goal;
        if (!n || !goal || n.fixed) {
          if (n) n.goal = null;
          gliding.delete(id);
          continue;
        }
        n.x += (goal.x - n.x) * t;
        n.y += (goal.y - n.y) * t;
        if (Math.abs(goal.x - n.x) < 0.3 && Math.abs(goal.y - n.y) < 0.3) {
          Object.assign(n, { x: goal.x, y: goal.y, goal: null });
          gliding.delete(id);
        }
      }
      scene.layoutGroups();
      scene.redraw = true;
      return gliding.size > 0;
    },
  };
}
